import { useTranslation } from 'react-i18next'
import { useFetcher, useFetchers } from 'react-router'

export type CartRecord = Record<string, number>

/**
 * Every cart mutation shares ONE fetcher via this key. The cart cookie is a
 * whole-record read-modify-write on the server, so two concurrent writes
 * silently drop each other's changes (last Set-Cookie wins). A single
 * fetcher + disabled controls while it's in flight keeps writes serialized;
 * optimistic display keeps the UI instant.
 */
export const CART_FETCHER_KEY = 'cart'

export function useCartFetcher() {
	return useFetcher({ key: CART_FETCHER_KEY })
}

/**
 * Cart with in-flight fetcher submissions applied, so steppers and the cart
 * bar respond instantly. Steppers submit ABSOLUTE quantities
 * (intent=update-quantity): rapid clicks each target the correct next value
 * and the last request wins — no lost increments.
 */
export function useOptimisticCart(cart: CartRecord): CartRecord {
	const fetchers = useFetchers()
	const optimistic: CartRecord = { ...cart }
	for (const fetcher of fetchers) {
		if (!fetcher.formData) continue
		const productId = fetcher.formData.get('productId')
		if (typeof productId !== 'string') continue
		const intent = fetcher.formData.get('intent')
		if (intent === 'update-quantity' || intent === 'add-to-cart') {
			const quantity = Number(fetcher.formData.get('quantity') ?? 1)
			if (!Number.isFinite(quantity)) continue
			const next =
				intent === 'add-to-cart'
					? (optimistic[productId] ?? 0) + quantity
					: quantity
			if (next > 0) optimistic[productId] = next
			else delete optimistic[productId]
		} else if (intent === 'remove-from-cart') {
			delete optimistic[productId]
		}
	}
	return optimistic
}

/**
 * Plus/minus stepper writing straight to the cart via the /shop action.
 * Single click adds; quantities are editable inline wherever it renders.
 */
export function QuantityStepper({
	productId,
	slug,
	quantity,
	max = 99,
	action = '/shop',
	compact = false,
}: {
	productId: string
	slug: string
	quantity: number
	max?: number
	action?: string
	compact?: boolean
}) {
	const { t } = useTranslation()
	const fetcher = useCartFetcher()
	// Never two concurrent cart cookie writes: wait for the shared fetcher.
	const busy = fetcher.state !== 'idle'

	const submitQuantity = (next: number) => {
		void fetcher.submit(
			{
				intent: 'update-quantity',
				productId,
				quantity: String(Math.max(0, Math.min(next, max))),
			},
			{ method: 'POST', action },
		)
	}

	const size = compact ? 28 : 36
	const buttonStyle: React.CSSProperties = {
		width: size,
		height: size,
		borderRadius: '50%',
		border: '1px solid var(--line)',
		background: 'var(--paper)',
		color: 'var(--ink)',
		cursor: 'pointer',
		fontSize: compact ? 14 : 16,
		lineHeight: 1,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontFamily: 'var(--font-sans)',
	}

	return (
		<div className="inline-flex items-center gap-3">
			<button
				type="button"
				aria-label={t('shopDecrease')}
				data-testid={`shop-dec-${slug}`}
				onClick={() => submitQuantity(quantity - 1)}
				disabled={quantity <= 0 || busy}
				style={{ ...buttonStyle, opacity: quantity <= 0 ? 0.35 : 1 }}
			>
				−
			</button>
			<span
				data-testid={`shop-qty-${slug}`}
				className="min-w-[1.5ch] text-center tabular-nums"
				style={{ fontSize: compact ? 14 : 16 }}
			>
				{quantity}
			</span>
			<button
				type="button"
				aria-label={t('shopIncrease')}
				data-testid={`shop-inc-${slug}`}
				onClick={() => submitQuantity(quantity + 1)}
				disabled={quantity >= max || busy}
				style={{ ...buttonStyle, opacity: quantity >= max ? 0.35 : 1 }}
			>
				+
			</button>
		</div>
	)
}
